<template>
  <section class="section">
    <div class="container">
      <div class="columns">
        <div class="column">
          <h1 class="title">Create Event</h1>
          <p>
            Fill out the details below. Note that these are the required fields for making an event.
            After the event is created, you will be able to edit everything else.
          </p>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <div class="message is-danger" v-for="message in errors" v-bind:key="message">
            <div class="message-body">
              {{message}}
            </div>
          </div>

          <div class="field">
            <label class="label">Event Name</label>
            <div class="control">
              <input v-model="eventName" class="input" :class="!invalidEventName ? '' : 'is-danger'"
                  :disabled="loading"
                  type="text"
                  placeholder="">
            </div>
          </div>

          <label class="label">Start Date &amp; Time</label>
          <div class="field is-grouped">
            <div class="control has-icons-left">
              <input v-model="startDate" class="input" :class="!invalidStartDateTime ? '' : 'is-danger'"
                  :disabled="loading"
                  type="date">
              <span class="icon is-small is-left">
                <i class="fas fa-calendar"></i>
              </span>
            </div>
            <div class="control has-icons-left">
              <input v-model="startTime" class="input" :class="!invalidStartDateTime ? '' : 'is-danger'"
                  :disabled="loading"
                  type="time">
              <span class="icon is-small is-left">
                <i class="fas fa-clock"></i>
              </span>
            </div>
          </div>

          <label class="label">End Date &amp; Time</label>
          <div class="field is-grouped">
            <div class="control has-icons-left">
              <input v-model="endDate" class="input" :class="!invalidEndDateTime ? '' : 'is-danger'"
                  :disabled="loading"
                  type="date">
              <span class="icon is-small is-left">
                <i class="fas fa-calendar"></i>
              </span>
            </div>
            <div class="control has-icons-left">
              <input v-model="endTime" class="input" :class="!invalidEndDateTime ? '' : 'is-danger'"
                  :disabled="loading"
                  type="time">
              <span class="icon is-small is-left">
                <i class="fas fa-clock"></i>
              </span>
            </div>
          </div>

          <div class="field">
            <label class="label">Name of Address</label>
            <div class="control" :class="eventIsPrivate ? 'has-icons-left' : ''">
              <input v-model="eventAddressName" class="input" :class="!invalidAddressName ? '' : 'is-danger'"
                  :disabled="loading"
                  type="text"
                  placeholder="Chateau du Loft">
              <template v-if="eventIsPrivate">
                <span class="icon is-small is-left">
                  <i class="fas fa-lock"></i>
                </span>
              </template>
            </div>
          </div>

          <div class="field">
            <label class="label">Address</label>
            <div class="control" :class="eventIsPrivate ? 'has-icons-left' : ''">
              <input v-model="eventAddress" class="input" :class="!invalidAddress ? '' : 'is-danger'"
                  :disabled="loading"
                  type="text"
                  placeholder="1234 Spadina Ave, Toronto, Ontario">
              <template v-if="eventIsPrivate">
                <span class="icon is-small is-left">
                  <i class="fas fa-lock"></i>
                </span>
              </template>
            </div>
          </div>

          <div class="field">
            <div class="control">
              <label class="checkbox">
                <input type="checkbox" v-model="eventIsPrivate" :disabled="loading">
                This event is private (only vetted attendees will see address)
                <span class="icon is-small has-text-grey">
                  <i class="fas" :class="eventIsPrivate ? 'fa-lock' : 'fa-lock-open'"></i>
                </span>
              </label>
            </div>
          </div>

          <div class="field">
            <p class="control has-icons-left">
              <span class="select">
                <select v-model="eventCountry" :disabled="loading">
                  <option disabled>Country</option>
                  <option v-for="country in countries" v-bind:key="country">{{country}}</option>
                </select>
              </span>
              <span class="icon is-small is-left">
                <i class="fas fa-globe"></i>
              </span>
            </p>
          </div>
          <div class="field">
            <p class="control has-icons-left">
              <span class="select">
                <select v-model="eventState" :disabled="loading">
                  <option disabled>State/Province</option>
                  <option v-for="state in states" v-bind:key="state">{{state}}</option>
                </select>
              </span>
              <span class="icon is-small is-left">
                <i class="fas fa-map"></i>
              </span>
            </p>
          </div>
          <div class="field">
            <p class="control has-icons-left">
              <span class="select">
                <select v-model="eventCity" :disabled="loading">
                  <option disabled>City</option>
                  <option v-for="city in cities" v-bind:key="city">{{city}}</option>
                </select>
              </span>
              <span class="icon is-small is-left">
                <i class="fas fa-map-marker"></i>
              </span>
            </p>
          </div>

          <div class="field">
            <label class="label">Cost</label>
            <div class="control has-icons-left">
              <input v-model="cost" class="input" :class="!invalidCost ? '' : 'is-danger'"
                  :disabled="loading"
                  type="text"
                  placeholder="Free">
              <span class="icon is-small is-left">
                <i class="fas fa-money-bill"></i>
              </span>
            </div>
          </div>

          <div class="field">
            <p class="has-text-grey">
              After creating the event, there will be more things you can edit!
            </p>
          </div>

          <div class="field is-grouped">
            <div class="control">
              <button class="button is-primary" :class="loading ? 'is-loading' : ''" v-on:click="createEvent">Create &amp; Edit</button>
            </div>
            <div class="control">
              <router-link to="/" class="button is-text">Cancel</router-link>
            </div>
          </div>

          <div class="message is-danger" v-show="errors.length > 0">
            <div class="message-body">
              There are errors during the request; please fix them (see above) and try submitting again.
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import Locations from '@/locationData';
import API from '@/api';

export default {
  name: 'CreateEvents',
  props: [],
  data: function() {
    return {
      eventName: '',
      startDate: this.getLocalDateNow(0).toISOString().split('T')[0],
      startTime: this.getLocalDateNow(0).toISOString().split('T')[1].split(':', 2).join(':'),
      endDate: this.getLocalDateNow(1).toISOString().split('T')[0],
      endTime: this.getLocalDateNow(1).toISOString().split('T')[1].split(':', 2).join(':'),
      eventAddressName: '',
      eventAddress: '',
      eventIsPrivate: '',
      cost: '',
      eventCountry: 'Country',
      eventState: 'State/Province',
      eventCity: 'City',
      countries: Locations.Countries,
      states: [],
      cities: [],
      errors: [],
      loading: false,
    };
  },
  computed: {
    invalidEventName() {
      return this.eventName ? undefined : 'Event name must not be empty';
    },
    invalidStartDateTime() {
      return undefined;
    },
    invalidEndDateTime() {
      return undefined;
    },
    invalidAddressName() {
      return this.eventAddressName ? undefined : 'Event address name must not be empty';
    },
    invalidAddress() {
      return this.eventAddress ? undefined : 'Event address must not be empty';
    },
    invalidCost() {
      return this.cost ? undefined : 'Event cost must not be empty';
    }
  },
  methods: {
    getLocalDateNow(daysOffset) {
      const nowUTC = new Date();
      if (daysOffset) {
        nowUTC.setDate(nowUTC.getDate() + daysOffset);
      }
      return new Date(nowUTC.getTime() - nowUTC.getTimezoneOffset() * 60000);
    },
    async createEvent() {
      this.loading = true;
      this.errors = [];

      for (const check of [
          this.invalidEventName,
          this.invalidStartDateTime,
          this.invalidEndDateTime,
          this.invalidAddressName,
          this.invalidAddress,
          this.invalidCost]) {
        if (check) {
          this.errors.push(check);
        }
      }

      if (this.errors.length > 0) {
        this.loading = false;
        return;
      }

      const startDateTime = new Date(`${this.startDate} ${this.startTime}`).toISOString();
      const endDateTime = new Date(`${this.endDate} ${this.endTime}`).toISOString();

      if (await API.guard()) {
        const body = {
          title: this.eventName,
          tagline: '',
          description: '',
          cost: this.cost,
          startDate: startDateTime,
          endDate: endDateTime,
          location: {
            country: this.eventCountry,
            state: this.eventState,
            city: this.eventCity,
          },
          admins: []
        };
        if (this.eventIsPrivate) {
          body.publicAddressName = 'Private Address';
          body.publicAddress = '';
          body.privateAddressName = this.eventAddressName;
          body.privateAddress = this.eventAddress;
        } else {
          body.publicAddressName = this.eventAddressName;
          body.publicAddress = this.eventAddress;
          body.privateAddressName = '';
          body.privateAddress = '';
        }

        API.post('/event', {
          body,
        }).then((response) => {
          this.$router.push({
            path: `/event/${response.id}`,
          });
        }).catch((err) => {
          this.errors = [
            (err.response && err.response.data && err.response.data.error) || err.message,
          ];
          this.loading = false;
        });
      }
    },
  },
}
</script>